import { useSelect } from "downshift";
import styles from "./Select.module.scss";
import { Check, ChevronDown, ChevronUp, Close } from "../Icon/Icon";
import { autoUpdate, flip, offset, useFloating } from "@floating-ui/react";
import classNames from "classnames";
import InputWrapper, { InputWrapperProps } from "../InputWrapper/InputWrapper";
import { forwardRef } from "react";
import OptionalPortal from "../util/OptionalPortal";
import IconButton from "../IconButton/IconButton";

type SelectItem = {
  value: string;
  label: string;
  shortLabel?: string;
  disabled?: boolean;
  sublabel?: string;
};

type SelectProps = {
  data: SelectItem[];
  onChange?: (value?: string | null) => void;
  onBlur?: () => void;
  value?: string | null;
  placeholder?: React.ReactNode;
  withinPortal?: boolean | HTMLElement;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  size?: InputWrapperProps["size"];
  loading?: boolean;
  loadingMessage?: string;
  useShortLabels?: boolean;
  matchWidth?: boolean;
};

const itemToString = (item: SelectItem | null) => item?.value ?? "";

const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    data,
    onChange,
    onBlur,
    value,
    placeholder,
    withinPortal,
    className,
    disabled,
    clearable = false,
    size = "medium",
    loading,
    loadingMessage,
    useShortLabels,
    matchWidth = true,
  },
  ref,
) {
  const selectedItem = data.find((item) => item.value === value) ?? null;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect<SelectItem>({
    selectedItem,
    items: data,
    itemToString,
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange?.(newSelectedItem?.value);
    },
    isItemDisabled: (item) => !!item.disabled,
  });

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [flip(), offset(4)],
  });

  return (
    <div
      className={classNames(styles.container, styles[size], className)}
      ref={ref}
    >
      <div className={styles.inputWrapper} ref={refs.setReference}>
        <InputWrapper
          size={size}
          disabled={disabled}
          loading={loading}
          loadingMessage={loadingMessage}
          rightSection={
            <div className={styles.icons}>
              {clearable && selectedItem && (
                <IconButton
                  className={styles.clearButton}
                  variant="unstyled"
                  aria-label="Clear"
                  IconComponent={Close}
                  iconSize={size === "small" ? "xsmall" : "small"}
                  size="xsmall"
                  onClick={() => onChange?.(null)}
                />
              )}
              {isOpen ? (
                <ChevronUp size="medium" />
              ) : (
                <ChevronDown size="medium" />
              )}
            </div>
          }
        >
          <div
            className={styles.trigger}
            aria-label="toggle menu"
            {...getToggleButtonProps({
              onBlur,
              disabled,
              id: "tag-select",
            })}
          >
            {selectedItem ? (
              <>
                {useShortLabels ? selectedItem.shortLabel : selectedItem.label}
                {selectedItem.sublabel && (
                  <span className={styles.sublabel}>
                    {selectedItem.sublabel}
                  </span>
                )}
              </>
            ) : (
              <span className={styles.inputPlaceholder}>
                {loading ? null : placeholder}
              </span>
            )}
          </div>
        </InputWrapper>
      </div>
      <OptionalPortal withinPortal={withinPortal}>
        <ul
          {...getMenuProps(
            { ref: refs.setFloating },
            { suppressRefError: true },
          )}
          style={{
            ...floatingStyles,
            width: matchWidth ? "100%" : "auto",
            maxWidth: matchWidth
              ? refs.reference.current?.getBoundingClientRect().width
              : undefined,
          }}
          className={classNames(styles.menu, {
            [styles.menuClosed]: !isOpen,
          })}
        >
          {isOpen &&
            data?.map((item, index) => (
              <li
                className={classNames(styles.menuItem, {
                  [styles.menuItemSelected]: highlightedIndex === index,
                })}
                key={item.value}
                {...getItemProps({
                  item,
                  index,
                })}
              >
                {item.label}
                {item.sublabel && (
                  <span className={styles.sublabel}>{item.sublabel}</span>
                )}
                {selectedItem?.value === item.value && (
                  <Check size="medium" className={styles.check} />
                )}
              </li>
            ))}
        </ul>
      </OptionalPortal>
    </div>
  );
});

export default Select;
